import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import {
    getVehicles, deleteVehicle,
    getServiceLocations, getVehicleMakes, getVehicleModels,
    getFuelTypes, getStatuses, getCompanies,
    getTransmissionTypes, getTaxStatuses,
    bulkUploadVehicles,
    getFinancialYears,
} from "../../services/vehicle_service";
import { getCurrentUser } from "../../services/auth_service";
import {
    isSuperadmin,
    hasPermission,
} from "../utils/permissions";
import vehicleDocumentsService from "../../services/vehicle_document_service";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import "./Vehicles.css";

import {FiChevronDown } from "react-icons/fi";

const API_BASE_URL = "https://srinivasafleet.com/api";
const INITIAL_FILTERS = {
    service_location_id: "",
    company_id: "",
    vehicle_make_id: "",
    vehicle_model_id: "",
    fuel_type_id: "",
    status_id: "",
    gps: "", 
    year_of_make: "",
    financial_year_id: "",
    active_status: "active",
};

const PAGE_SIZE = 20;
const TOAST_AUTO_DISMISS_MS = 3200;

const VIEW_KNOWN_KEYS = new Set([
    "id", "vehicle_code", "vehicle_registration_number", "vehicle_display_number",
    "service_location_id", "company_id", "vehicle_make_id", "vehicle_model_id",
    "fuel_type_id", "transmission_type_id", "average_mileage", "year_of_make",
    "seating_capacity", "tax_status_id", "engine_number", "chassis_number",
    "gps_enabled", "status_id", "vehicle_photo", "created_at", "updated_at",
    "financial_year_id", "created_by", "updated_by", "created_by_name", "updated_by_name",
]);


const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDateOnly = (d) => {
    if (!d) return "-";
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatISODate = (d) => {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isVehicleActive = (v) => {
    const val = v?.is_active;
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val === 1;
    if (typeof val === "string") return val.toLowerCase() === "true" || val === "1";
    return Boolean(val);
};
const VEHICLE_DOC_LABELS = {
    rc: "RC",
    permit: "Permit",
    fitness: "Fitness",
    insurance: "Insurance",
    tax: "Road Tax",
    pollution: "PUC",
    police_sticker: "Police Sticker",
    city_police_permit: "City Police Permit",
};
const VEHICLE_DOC_KEYS = Object.keys(VEHICLE_DOC_LABELS);
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?.*)?$/i;
const PDF_EXT_RE = /\.pdf(\?.*)?$/i;

const isImageDocUrl = (url) => IMAGE_EXT_RE.test(url || "");
const isPdfDocUrl = (url) => PDF_EXT_RE.test(url || "");

const getDocumentExpiryClass = (expiry) => {
    if (!expiry) return "";
    const expiryDate = new Date(expiry);
    if (Number.isNaN(expiryDate.getTime())) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = Math.ceil((expiryDate - today) / 86400000);

    if (days < 0) return "expired";
    if (days <= 30) return "expiring";
    return "valid";
};

export default function Vehicles() {
    const navigate = useNavigate();
    const isSuperadminUser = isSuperadmin();
    const canViewVehicles =
        isSuperadminUser || hasPermission("vehicles", "view");
    const canEditVehicles =
        isSuperadminUser || hasPermission("vehicles", "edit");
    const canDeleteVehicles =
        isSuperadminUser || hasPermission("vehicles", "delete");
    const canManage = canEditVehicles || canDeleteVehicles;
    const confirm = useConfirm();
    const mastersLoaded = useRef(false);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [serviceLocations, setServiceLocations] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [transmissionTypes, setTransmissionTypes] = useState([]);
    const [taxStatuses, setTaxStatuses] = useState([]);
    const [search, setSearch] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const filterWrapperRef = useRef(null);
    const [showDateRange, setShowDateRange] = useState(false);
    const dateRangeWrapperRef = useRef(null);
    const [viewVehicle, setViewVehicle] = useState(null);
    const [viewVehicleDocs, setViewVehicleDocs] = useState(null);
    const [viewVehicleDocsLoading, setViewVehicleDocsLoading] = useState(false);
    const [viewVehicleTab, setViewVehicleTab] = useState("information");
    const [docViewer, setDocViewer] = useState(null);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [financialYears, setFinancialYears] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const actionMenuRef = useRef(null);
    const actionButtonRef = useRef(null);
    const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, width: 150 });
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);

    const showToast = (type, text) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ type, text });
        toastTimerRef.current = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
    };

    const dismissToast = () => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast(null);
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    const fetchFinancialYears = async () => {
        try { setFinancialYears(await getFinancialYears() || []); } catch (e) { console.error(e); }
    };

    
    
    
    
    
    
    

    const loadVehicles = async () => {
        try {
            setLoading(true);
            const data = await getVehicles("all");
            setVehicles(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadMasters = async () => {
        const [locations, makes, models, fuelData, statusData, companyData, transmissionData, taxData] =
            await Promise.allSettled([
                getServiceLocations(), getVehicleMakes(), getVehicleModels(),
                getFuelTypes(), getStatuses(), getCompanies(),
                getTransmissionTypes(), getTaxStatuses(),
            ]);

        if (locations.status === "fulfilled") setServiceLocations(locations.value);
        if (makes.status === "fulfilled") setVehicleMakes(makes.value);
        if (models.status === "fulfilled") setVehicleModels(models.value);
        if (fuelData.status === "fulfilled") setFuelTypes(fuelData.value);
        if (statusData.status === "fulfilled") setStatuses(statusData.value);
        if (companyData.status === "fulfilled") setCompanies(companyData.value);
        if (transmissionData.status === "fulfilled") setTransmissionTypes(transmissionData.value);
        if (taxData.status === "fulfilled") setTaxStatuses(taxData.value);
    };

    useEffect(() => {
        loadVehicles();
        fetchFinancialYears();
        if (!mastersLoaded.current) {
            mastersLoaded.current = true;
            loadMasters();
        }
    }, []);

    useEffect(() => {
        if (filters.vehicle_make_id) {
            getVehicleModels(filters.vehicle_make_id)
                .then(setModelOptions)
                .catch(() => setModelOptions([]));
        } else {
            setModelOptions(vehicleModels);
        }
    }, [filters.vehicle_make_id, vehicleModels]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key !== "Escape") return;
            if (docViewer) { setDocViewer(null); return; }
            if (lightbox) { setLightbox(null); return; }
            if (openActionMenu) { setOpenActionMenu(null); return; }
            if (viewVehicle) setViewVehicle(null);
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [docViewer, lightbox, viewVehicle, openActionMenu]);

    useEffect(() => {
        document.body.style.overflow = viewVehicle || docViewer ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [viewVehicle, docViewer]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, filters, dateFrom, dateTo]);
    useEffect(() => {
        const handleFocus = () => {
            loadVehicles();
        };
        window.addEventListener("focus", handleFocus);
        return () => window.removeEventListener("focus", handleFocus);
    }, []);

    useEffect(() => {
        if (!openActionMenu) return;

        const handleClickOutside = (e) => {
            const button = actionButtonRef.current;
            const menu = actionMenuRef.current;

            if (
                button &&
                !button.contains(e.target) &&
                menu &&
                !menu.contains(e.target)
            ) {
                setOpenActionMenu(null);
            }
        };

        const handleReposition = () => {
            const button = actionButtonRef.current;
            if (!button) return;

            const rect = button.getBoundingClientRect();
            const menuWidth = 150;
            const menuHeight = 130;
            const gap = 6;

            let left = rect.right - menuWidth;
            let top = rect.bottom + gap;

            if (left < 8) left = 8;
            if (left + menuWidth > window.innerWidth - 8) {
                left = window.innerWidth - menuWidth - 8;
            }

            if (top + menuHeight > window.innerHeight - 8) {
                top = rect.top - menuHeight - gap;
            }

            if (top < 8) top = 8;

            setActionMenuPos({
                top,
                left,
                width: Math.min(menuWidth, window.innerWidth - 16),
            });
        };

        handleReposition();
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [openActionMenu]);

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: "Delete Vehicle",
            message: "Are you sure you want to delete this vehicle? This action cannot be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            danger: true,
        });
        if (!confirmed) return;

        try {
            await deleteVehicle(id);
            showToast("success", "Vehicle deleted successfully");
            loadVehicles();
        } catch {
            showToast("error", "Delete failed. Please try again.");
        }
    };

    const TEMPLATE_PATH = "/templates/vehicle_upload_template.xlsx";

    const handleDownloadTemplate = async () => {
        try {
            const res = await fetch(TEMPLATE_PATH);
            if (!res.ok) throw new Error(`Status ${res.status}`);

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "vehicle_upload_template.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Template download failed:", err);
            alert(
                `Couldn't download the template.\n\n` +
                `Make sure the file exists at:\n` +
                `  public${TEMPLATE_PATH}\n\n` +
                `(in your frontend project's public folder — check the filename and folder casing match exactly)`
            );
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setUploadResult(null);
        try {
            const result = await bulkUploadVehicles(file);
            setUploadResult(result);
            loadVehicles();
        } catch (err) {
            alert(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const resolve = (arr, id, field = "name") =>
        arr.find((x) => String(x.id) === String(id))?.[field] || "—";

    const photoUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http")) return path;
        return `${API_BASE_URL}${path}`;
    };

    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
        setDateFrom("");
        setDateTo("");
    };
    const clearOneFilter = (key) => {
        setFilters((f) => ({ ...f, [key]: "" }));
        if (key === "financial_year_id") {
            setDateFrom("");
            setDateTo("");
            setShowDateRange(false);
        }
    };

    useEffect(() => {
        if (!showFilters) return;
        const handleClickOutside = (e) => {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(e.target)) {
                setShowFilters(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showFilters]);

    useEffect(() => {
        if (!showDateRange) return;
        const handleClickOutside = (e) => {
            if (dateRangeWrapperRef.current && !dateRangeWrapperRef.current.contains(e.target)) {
                setShowDateRange(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showDateRange]);

    const activeFilterCount = Object.entries(filters).filter(
        ([k, v]) => v && !(k === "active_status" && v === "active")
    ).length;

    const yearOptions = useMemo(
        () =>
            Array.from(new Set(vehicles.map((v) => v.year_of_make).filter(Boolean))).sort(
                (a, b) => b - a
            ),
        [vehicles]
    );

    const matchesFinancialYear = (v, selectedId) => {
        if (!selectedId) return true;
        return String(v.financial_year_id) === String(selectedId);
    };
    const fyActualRange = useMemo(() => {
        if (!filters.financial_year_id) return null;
        const matched = vehicles.filter((v) => matchesFinancialYear(v, filters.financial_year_id));
        const dates = matched
            .map((v) => (v.created_at ? new Date(v.created_at) : null))
            .filter((d) => d instanceof Date && !isNaN(d.getTime()));
        if (dates.length === 0) return null;
        return {
            start: new Date(Math.min(...dates)),
            end: new Date(Math.max(...dates)),
        };
    }, [filters.financial_year_id, vehicles]);

    const filterChips = useMemo(() => {
        const chips = [];
        if (filters.service_location_id) {
            chips.push({ key: "service_location_id", label: `Location: ${resolve(serviceLocations, filters.service_location_id)}` });
        }
        if (filters.company_id) {
            chips.push({ key: "company_id", label: `Company: ${resolve(companies, filters.company_id)}` });
        }
        if (filters.vehicle_make_id) {
            chips.push({ key: "vehicle_make_id", label: `Make: ${resolve(vehicleMakes, filters.vehicle_make_id)}` });
        }
        if (filters.vehicle_model_id) {
            chips.push({ key: "vehicle_model_id", label: `Model: ${resolve(vehicleModels, filters.vehicle_model_id)}` });
        }
        if (filters.fuel_type_id) {
            chips.push({ key: "fuel_type_id", label: `Fuel: ${resolve(fuelTypes, filters.fuel_type_id)}` });
        }
        if (filters.status_id) {
            chips.push({ key: "status_id", label: `Status: ${resolve(statuses, filters.status_id)}` });
        }
        if (filters.gps) {
            chips.push({ key: "gps", label: `GPS: ${filters.gps === "yes" ? "Enabled" : "Not Enabled"}` });
        }
        if (filters.year_of_make) {
            chips.push({ key: "year_of_make", label: `Year: ${filters.year_of_make}` });
        }
        if (filters.active_status && filters.active_status !== "active") {
            chips.push({
                key: "active_status",
                label: `Active Status: ${filters.active_status === "inactive" ? "Inactive" : "All"}`,
            });
        }
        return chips;
    }, [filters, serviceLocations, companies, vehicleMakes, vehicleModels, fuelTypes, statuses]);

    const filteredVehicles = vehicles.filter((v) => {

        const text = search.toLowerCase();
        const matchesSearch = !text || (
            v.vehicle_registration_number?.toLowerCase().includes(text) ||
            v.vehicle_display_number?.toLowerCase().includes(text) ||
            resolve(serviceLocations, v.service_location_id).toLowerCase().includes(text) ||
            resolve(vehicleMakes, v.vehicle_make_id).toLowerCase().includes(text) ||
            resolve(companies, v.company_id).toLowerCase().includes(text)
        );
        if (!matchesSearch) return false;

        if (filters.service_location_id && String(v.service_location_id) !== filters.service_location_id) return false;
        if (filters.company_id && String(v.company_id) !== filters.company_id) return false;
        if (filters.vehicle_make_id && String(v.vehicle_make_id) !== filters.vehicle_make_id) return false;

        if (filters.active_status) {
            const wantActive = filters.active_status === "active";
            if (isVehicleActive(v) !== wantActive) return false;
        }

        if (filters.vehicle_model_id && String(v.vehicle_model_id) !== filters.vehicle_model_id) return false;
        if (filters.fuel_type_id && String(v.fuel_type_id) !== filters.fuel_type_id) return false;
        if (filters.status_id && String(v.status_id) !== filters.status_id) return false;
        if (filters.gps === "yes" && !v.gps_enabled) return false;
        if (filters.gps === "no" && v.gps_enabled) return false;
        if (filters.year_of_make && String(v.year_of_make) !== filters.year_of_make) return false;

        if (!matchesFinancialYear(v, filters.financial_year_id)) return false;

        if (filters.financial_year_id && (dateFrom || dateTo)) {
            const createdAt = v.created_at ? new Date(v.created_at) : null;
            if (!createdAt) return false;
            if (dateFrom) {
                const from = new Date(dateFrom);
                from.setHours(0, 0, 0, 0);
                if (createdAt < from) return false;
            }
            if (dateTo) {
                const to = new Date(dateTo);
                to.setHours(23, 59, 59, 999);
                if (createdAt > to) return false;
            }
        }

        return true;
    });

    const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / PAGE_SIZE));

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    const paginatedVehicles = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredVehicles.slice(start, start + PAGE_SIZE);
    }, [filteredVehicles, currentPage]);

    const goToPage = (page) => {
        setCurrentPage(Math.min(Math.max(1, page), totalPages));
    };

    
    
    const gpsCount = filteredVehicles.filter((v) => v.gps_enabled).length;

    const evFuelIds = fuelTypes
        .filter((f) => f.name.toLowerCase().includes("electric"))
        .map((f) => String(f.id));

    const evCount = filteredVehicles.filter((v) => evFuelIds.includes(String(v.fuel_type_id))).length;
    const iceCount = filteredVehicles.filter((v) => !evFuelIds.includes(String(v.fuel_type_id))).length;
    const openViewModal = (vehicle) => {
        setViewVehicle(vehicle);
        setViewVehicleTab("information");
        setViewVehicleDocs(null);
        setViewVehicleDocsLoading(true);
        setDocViewer(null);
    };

    const closeViewModal = () => {
        setDocViewer(null);
        setViewVehicle(null);
        setViewVehicleTab("information");
        setViewVehicleDocs(null);
        setViewVehicleDocsLoading(false);
    };

    const openDocumentViewer = (document) => {
        if (!document?.url) return;
        setDocViewer(document);
    };

    const closeDocumentViewer = () => {
        setDocViewer(null);
    };

    const getDocumentFileName = (document) => {
        const vehicleName = (
            viewVehicle?.vehicle_registration_number ||
            viewVehicle?.vehicle_code ||
            "vehicle"
        )
            .toString()
            .trim()
            .replace(/[^a-z0-9_-]+/gi, "_");

        const documentName = (document?.label || "document")
            .toString()
            .trim()
            .replace(/[^a-z0-9_-]+/gi, "_");

        let extension = "";
        try {
            const pathname = new URL(document?.url || "").pathname;
            const match = pathname.match(/\.([a-z0-9]{2,5})$/i);
            if (match) extension = `.${match[1].toLowerCase()}`;
        } catch {
            
        }

        if (!extension) {
            if (document?.isPdf) extension = ".pdf";
            else if (document?.isImage) extension = ".jpg";
            else extension = ".pdf";
        }

        return `${vehicleName}_${documentName}${extension}`;
    };

    const handleDocumentDownload = async () => {
        if (!docViewer?.url) return;

        try {
            const response = await fetch(docViewer.url, {
                method: "GET",
                mode: "cors",
                cache: "no-store",
            });

            if (!response.ok) {
                throw new Error(`Download failed with status ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = getDocumentFileName(docViewer);
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            console.error("Vehicle document download failed:", error);

            
            const link = document.createElement("a");
            link.href = docViewer.url;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.download = getDocumentFileName(docViewer);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleDocumentPrint = async () => {
        if (!docViewer?.url) return;

        try {
            const response = await fetch(docViewer.url, {
                method: "GET",
                mode: "cors",
                cache: "no-store",
            });

            if (!response.ok) {
                throw new Error(`Print preparation failed with status ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            if (docViewer.isImage) {
                const printWindow = window.open("", "_blank", "width=1100,height=850");

                if (!printWindow) {
                    window.URL.revokeObjectURL(blobUrl);
                    alert("Please allow pop-ups for printing this document.");
                    return;
                }

                printWindow.document.write(`
                    <!doctype html>
                    <html>
                    <head>
                        <title>${docViewer.label || "Vehicle Document"}</title>
                        <style>
                            html, body {
                                margin: 0;
                                padding: 0;
                                background: #fff;
                            }
                            body {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                min-height: 100vh;
                            }
                            img {
                                max-width: 100%;
                                max-height: 100vh;
                                object-fit: contain;
                            }
                            @media print {
                                body { min-height: auto; }
                                img {
                                    width: 100%;
                                    max-height: none;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <img id="print-document" src="${blobUrl}" alt="${docViewer.label || "Vehicle Document"}" />
                    </body>
                    </html>
                `);
                printWindow.document.close();

                const image = printWindow.document.getElementById("print-document");
                image.onload = () => {
                    printWindow.focus();
                    printWindow.print();
                };

                setTimeout(() => {
                    try {
                        printWindow.focus();
                        printWindow.print();
                    } catch {
                        
                    }
                }, 1000);

                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
                return;
            }

            
            const printWindow = window.open("", "_blank", "width=1100,height=850");

            if (!printWindow) {
                window.URL.revokeObjectURL(blobUrl);
                alert("Please allow pop-ups for printing this document.");
                return;
            }

            printWindow.document.write(`
                <!doctype html>
                <html>
                <head>
                    <title>${docViewer.label || "Vehicle Document"}</title>
                    <style>
                        html, body {
                            margin: 0;
                            width: 100%;
                            height: 100%;
                            overflow: hidden;
                            background: #fff;
                        }
                        iframe {
                            width: 100%;
                            height: 100%;
                            border: 0;
                        }
                    </style>
                </head>
                <body>
                    <iframe id="print-frame" src="${blobUrl}" title="${docViewer.label || "Vehicle Document"}"></iframe>
                </body>
                </html>
            `);
            printWindow.document.close();

            setTimeout(() => {
                try {
                    printWindow.focus();
                    printWindow.print();
                } catch (error) {
                    console.warn("Native PDF print dialog could not be opened automatically:", error);
                }
            }, 1800);

            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 15000);
        } catch (error) {
            console.error("Vehicle document print failed:", error);

            
            
            const printWindow = window.open(docViewer.url, "_blank", "noopener,noreferrer");
            if (!printWindow) {
                alert("Please allow pop-ups for printing this document.");
            }
        }
    };

    
    useEffect(() => {
        let cancelled = false;

        const loadVehicleDocuments = async () => {
            if (!viewVehicle?.id) {
                setViewVehicleDocs(null);
                setViewVehicleDocsLoading(false);
                return;
            }

            setViewVehicleDocsLoading(true);
            try {
                const data = await vehicleDocumentsService.getVehicleDocuments(viewVehicle.id);
                if (!cancelled) {
                    setViewVehicleDocs(data || null);
                }
            } catch (error) {
                console.error("Failed to load vehicle documents:", error);
                if (!cancelled) {
                    setViewVehicleDocs(null);
                }
            } finally {
                if (!cancelled) {
                    setViewVehicleDocsLoading(false);
                }
            }
        };

        loadVehicleDocuments();

        return () => {
            cancelled = true;
        };
    }, [viewVehicle?.id]);
    const waitForRender = () =>
        new Promise((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
            });
        });

    const waitForSelector = async (selector, timeout = 6000) => {
        const started = Date.now();

        while (Date.now() - started < timeout) {
            const element = document.querySelector(selector);
            if (element) return element;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        return null;
    };

    const prepareVehiclePrintClone = async () => {
        if (!viewVehicle) return null;

        const originalTab = viewVehicleTab;

        try {
            setViewVehicleTab("information");
            await waitForRender();

            const modal = document.querySelector(".vv2-modal");

            if (!modal) {
                throw new Error("Vehicle details are not ready.");
            }

            const modalClone = modal.cloneNode(true);

            modalClone.querySelectorAll(
                ".vv2-header-actions, .vv2-tabs, .vv2-close-btn, .vv2-document-actions"
            ).forEach((el) => el.remove());

            const cloneTabContent = modalClone.querySelector(".vv2-tab-content");
            const cloneInfoBody = modalClone.querySelector(".vv2-body");

            if (cloneTabContent && cloneInfoBody) {
                cloneTabContent.replaceChildren(cloneInfoBody);
            }

            setViewVehicleTab("documents");
            await waitForRender();

            const documentsPanel = await waitForSelector(
                ".vv2-documents-panel",
                6000
            );

            if (documentsPanel && cloneTabContent) {
                const documentsClone = documentsPanel.cloneNode(true);

                documentsClone
                    .querySelectorAll(".vv2-document-actions")
                    .forEach((el) => el.remove());

                cloneTabContent.appendChild(documentsClone);
            }

            modalClone.classList.add("vv2-print-clone");

            const printBody = modalClone.querySelector(".vv2-body");
            if (printBody) {
                printBody.style.setProperty("display", "block", "important");
                printBody.style.setProperty("width", "100%", "important");
                printBody.style.setProperty("padding", "18px 20px 22px", "important");
                printBody.style.setProperty("background", "#ffffff", "important");
            }

            const printPhotoCol = modalClone.querySelector(".vv2-photo-col");
            if (printPhotoCol) {
                printPhotoCol.style.setProperty("width", "220px", "important");
                printPhotoCol.style.setProperty("max-width", "220px", "important");
                printPhotoCol.style.setProperty("margin", "0 auto 18px", "important");
            }

            modalClone.querySelectorAll(
                ".vv2-body, .vv2-documents-panel, .vv2-tab-content"
            ).forEach((el) => {
                el.style.setProperty("max-height", "none", "important");
                el.style.setProperty("height", "auto", "important");
                el.style.setProperty("min-height", "0", "important");
                el.style.setProperty("overflow", "visible", "important");
            });

            modalClone.querySelectorAll(
                ".vv2-field, .vv2-section, .vv2-document-row, .vv2-documents-intro"
            ).forEach((el) => {
                el.style.breakInside = "avoid";
                el.style.pageBreakInside = "avoid";
            });

            modalClone.style.setProperty("width", "100%", "important");
            modalClone.style.setProperty("max-width", "900px", "important");
            modalClone.style.setProperty("max-height", "none", "important");
            modalClone.style.setProperty("height", "auto", "important");
            modalClone.style.setProperty("overflow", "visible", "important");
            modalClone.style.setProperty("border-radius", "0", "important");
            modalClone.style.setProperty("box-shadow", "none", "important");
            modalClone.style.setProperty("background", "#ffffff", "important");

            return modalClone;
        } finally {
            setViewVehicleTab(originalTab);
        }
    };

    const createPdfFromElement = async (element, fileName) => {
        const host = document.createElement("div");

        host.style.position = "fixed";
        host.style.left = "-100000px";
        host.style.top = "0";
        host.style.width = "900px";
        host.style.minHeight = "1px";
        host.style.background = "#ffffff";
        host.style.padding = "0";
        host.style.margin = "0";
        host.style.opacity = "1";
        host.style.pointerEvents = "none";
        host.style.zIndex = "-100000";

        const pdfStyle = document.createElement("style");
        pdfStyle.textContent = `
            .vv2-print-clone.vv2-modal {
                width: 900px !important;
                max-width: 900px !important;
                min-width: 900px !important;
                max-height: none !important;
                height: auto !important;
                overflow: visible !important;
                display: block !important;
                background: #ffffff !important;
                border-radius: 0 !important;
                box-shadow: none !important;
            }

            .vv2-print-clone .vv2-tab-content,
            .vv2-print-clone .vv2-body,
            .vv2-print-clone .vv2-documents-panel {
                height: auto !important;
                min-height: 0 !important;
                max-height: none !important;
                overflow: visible !important;
                background: #ffffff !important;
            }

            .vv2-print-clone .vv2-body {
                display: block !important;
                width: 100% !important;
                padding: 18px 20px 22px !important;
                background: #ffffff !important;
            }

            .vv2-print-clone .vv2-photo-col {
                width: 220px !important;
                max-width: 220px !important;
                margin: 0 auto 18px !important;
            }

            .vv2-print-clone .vv2-grid {
                grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                gap: 8px 10px !important;
            }

            .vv2-print-clone .vv2-section,
            .vv2-print-clone .vv2-field,
            .vv2-print-clone .vv2-document-row,
            .vv2-print-clone .vv2-documents-intro {
                background: #ffffff !important;
                background-image: none !important;
            }

            .vv2-print-clone .vv2-section {
                border: 1px solid #e5eaf2 !important;
                box-shadow: none !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }

            .vv2-print-clone .vv2-field {
                border: 1px solid #e5eaf2 !important;
                box-shadow: none !important;
                min-width: 0 !important;
            }

            .vv2-print-clone .vv2-document-row,
            .vv2-print-clone .vv2-documents-intro {
                border: 1px solid #e5eaf2 !important;
                box-shadow: none !important;
            }

            .vv2-print-clone .vv2-document-row {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }

            .vv2-print-clone .vv2-header {
                background: #0a1e4d !important;
                color: #ffffff !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }

            .vv2-print-clone .vv2-header::before,
            .vv2-print-clone .vv2-header::after {
                display: none !important;
            }

            .vv2-print-clone .vv2-header-title,
            .vv2-print-clone .vv2-header-sub {
                color: #ffffff !important;
            }

            .vv2-print-clone img {
                max-width: 100% !important;
            }
        `;

        host.appendChild(pdfStyle);
        host.appendChild(element);
        document.body.appendChild(host);

        try {
            await waitForRender();

            const images = Array.from(element.querySelectorAll("img"));

            await Promise.all(
                images.map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) {
                                resolve();
                                return;
                            }

                            img.addEventListener("load", resolve, { once: true });
                            img.addEventListener("error", resolve, { once: true });

                            setTimeout(resolve, 5000);
                        })
                )
            );

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                backgroundColor: "#ffffff",
                logging: false,
                imageTimeout: 15000,
                width: 900,
                windowWidth: 900,
                scrollX: 0,
                scrollY: 0,
            });

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "a4",
                compress: true,
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 24;
            const usableWidth = pageWidth - margin * 2;
            const usableHeight = pageHeight - margin * 2;

            const scale = usableWidth / canvas.width;
            const pixelsPerPage = Math.floor(usableHeight / scale);
            const pageCount = Math.max(
                1,
                Math.ceil(canvas.height / pixelsPerPage)
            );

            for (let page = 0; page < pageCount; page += 1) {
                if (page > 0) {
                    pdf.addPage();
                }

                const sourceY = page * pixelsPerPage;
                const sourceHeight = Math.min(
                    pixelsPerPage,
                    canvas.height - sourceY
                );

                const pageCanvas = document.createElement("canvas");
                pageCanvas.width = canvas.width;
                pageCanvas.height = sourceHeight;

                const pageContext = pageCanvas.getContext("2d");

                pageContext.fillStyle = "#ffffff";
                pageContext.fillRect(
                    0,
                    0,
                    pageCanvas.width,
                    pageCanvas.height
                );

                pageContext.drawImage(
                    canvas,
                    0,
                    sourceY,
                    canvas.width,
                    sourceHeight,
                    0,
                    0,
                    canvas.width,
                    sourceHeight
                );

                const pageImage = pageCanvas.toDataURL(
                    "image/jpeg",
                    0.96
                );

                const renderedHeight = sourceHeight * scale;

                pdf.addImage(
                    pageImage,
                    "JPEG",
                    margin,
                    margin,
                    usableWidth,
                    renderedHeight,
                    undefined,
                    "FAST"
                );
            }

            pdf.save(fileName);
        } finally {
            host.remove();
        }
    };

    const handleDownloadView = async () => {
        if (!viewVehicle || pdfGenerating) return;

        setPdfGenerating(true);

        try {
            const clone = await prepareVehiclePrintClone();

            if (!clone) {
                throw new Error("Vehicle details could not be prepared.");
            }

            const safeName = (
                viewVehicle.vehicle_registration_number ||
                viewVehicle.vehicle_code ||
                "vehicle"
            )
                .toString()
                .trim()
                .replace(/[^a-z0-9_-]+/gi, "_");

            await createPdfFromElement(clone, `${safeName}.pdf`);
        } catch (error) {
            console.error("Vehicle PDF download failed:", error);
            alert("Unable to generate the vehicle PDF. Please try again.");
        } finally {
            setPdfGenerating(false);
        }
    };

    const handlePrintView = async () => {
        if (!viewVehicle || printing) return;

        setPrinting(true);

        try {
            const clone = await prepareVehiclePrintClone();

            if (!clone) {
                throw new Error("Vehicle details could not be prepared.");
            }

            const printWindow = window.open("", "_blank", "width=1100,height=900");

            if (!printWindow) {
                throw new Error("Please allow pop-ups for printing.");
            }

            const styles = Array.from(
                document.querySelectorAll('link[rel="stylesheet"], style')
            )
                .map((node) => node.outerHTML)
                .join("\n");

            const title = (
                viewVehicle.vehicle_registration_number ||
                viewVehicle.vehicle_code ||
                "Vehicle"
            ).toString().replace(/[<>]/g, "");

            printWindow.document.open();
            printWindow.document.write(`
                <!doctype html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width,initial-scale=1">
                    <title>${title}</title>
                    ${styles}
                    <style>
                        @page {
                            size: A4 portrait;
                            margin: 10mm;
                        }

                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            min-height: 0 !important;
                            background: #fff !important;
                            background-color: #fff !important;
                        }

                        body {
                            display: block !important;
                            position: static !important;
                            overflow: visible !important;
                        }

                        #vehicle-print-root {
                            display: block !important;
                            width: 100% !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            background: #fff !important;
                        }

                        #vehicle-print-root .vv2-print-clone,
                        #vehicle-print-root .vv2-modal {
                            display: block !important;
                            position: static !important;
                            width: 100% !important;
                            max-width: none !important;
                            min-width: 0 !important;
                            height: auto !important;
                            min-height: 0 !important;
                            max-height: none !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            overflow: visible !important;
                            background: #fff !important;
                            box-shadow: none !important;
                            border: 0 !important;
                            border-radius: 0 !important;
                            float: none !important;
                            transform: none !important;
                            animation: none !important;
                            opacity: 1 !important;
                            visibility: visible !important;
                        }

                        #vehicle-print-root .vv2-header {
                            display: flex !important;
                            width: 100% !important;
                            background: #0a1e4d !important;
                            color: #fff !important;
                            break-after: avoid !important;
                            page-break-after: avoid !important;
                        }

                        #vehicle-print-root .vv2-tab-content,
                        #vehicle-print-root .vv2-body,
                        #vehicle-print-root .vv2-documents-panel {
                            display: block !important;
                            width: 100% !important;
                            height: auto !important;
                            min-height: 0 !important;
                            max-height: none !important;
                            overflow: visible !important;
                            background: #fff !important;
                        }

                        #vehicle-print-root .vv2-body {
                            padding: 8mm 0 5mm !important;
                        }

                        #vehicle-print-root .vv2-photo-col {
                            display: block !important;
                            width: 220px !important;
                            max-width: 220px !important;
                            margin: 0 auto 8mm !important;
                        }

                        #vehicle-print-root .vv2-section,
                        #vehicle-print-root .vv2-field,
                        #vehicle-print-root .vv2-document-row,
                        #vehicle-print-root .vv2-documents-intro {
                            background: #fff !important;
                            background-color: #fff !important;
                            background-image: none !important;
                            box-shadow: none !important;
                        }

                        #vehicle-print-root .vv2-section {
                            border: 1px solid #e5eaf2 !important;
                            break-inside: avoid !important;
                            page-break-inside: avoid !important;
                        }

                        #vehicle-print-root .vv2-field,
                        #vehicle-print-root .vv2-document-row {
                            border: 1px solid #e5eaf2 !important;
                        }

                        #vehicle-print-root .vv2-tabs,
                        #vehicle-print-root .vv2-header-actions,
                        #vehicle-print-root .vv2-close-btn,
                        #vehicle-print-root .vv2-document-actions {
                            display: none !important;
                        }

                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                    </style>
                </head>
                <body>
                    <main id="vehicle-print-root"></main>
                </body>
                </html>
            `);
            printWindow.document.close();

            const printRoot = printWindow.document.getElementById("vehicle-print-root");
            if (!printRoot) throw new Error("Print container could not be created.");

            printRoot.replaceChildren(clone);

            await new Promise((resolve) => {
                const check = () => {
                    const ready = printWindow.document.readyState === "complete";
                    const sheets = Array.from(printWindow.document.styleSheets || []);
                    const sheetsReady = sheets.every((sheet) => {
                        try {
                            return !!sheet.cssRules;
                        } catch {
                            return true;
                        }
                    });
                    if (ready && sheetsReady) resolve();
                    else setTimeout(check, 100);
                };
                check();
            });

            const images = Array.from(printWindow.document.images || []);
            await Promise.all(
                images.map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) {
                                resolve();
                                return;
                            }
                            img.addEventListener("load", resolve, { once: true });
                            img.addEventListener("error", resolve, { once: true });
                            setTimeout(resolve, 8000);
                        })
                )
            );

            try {
                if (printWindow.document.fonts?.ready) {
                    await printWindow.document.fonts.ready;
                }
            } catch {}

            await new Promise((resolve) =>
                printWindow.requestAnimationFrame(() =>
                    printWindow.requestAnimationFrame(() => setTimeout(resolve, 700))
                )
            );

            const printStyle = printWindow.document.createElement("style");
            printStyle.textContent = `
                @media print {
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #ffffff !important;
                    }
                    #vehicle-print-root {
                        display: block !important;
                        position: static !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #ffffff !important;
                    }
                    #vehicle-print-root .vv2-print-clone {
                        display: block !important;
                        position: static !important;
                        width: 100% !important;
                        max-width: none !important;
                        height: auto !important;
                        max-height: none !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        transform: none !important;
                        animation: none !important;
                        opacity: 1 !important;
                        visibility: visible !important;
                        background: #ffffff !important;
                    }
                    #vehicle-print-root .vv2-body,
                    #vehicle-print-root .vv2-tab-content,
                    #vehicle-print-root .vv2-documents-panel {
                        display: block !important;
                        width: 100% !important;
                        height: auto !important;
                        max-height: none !important;
                        overflow: visible !important;
                        background: #ffffff !important;
                    }
                    #vehicle-print-root .vv2-section,
                    #vehicle-print-root .vv2-document-row {
                        break-inside: avoid-page !important;
                        page-break-inside: avoid !important;
                    }
                }
            `;
            printWindow.document.head.appendChild(printStyle);

            await new Promise((resolve) => setTimeout(resolve, 300));
            printWindow.focus();
            printWindow.print();
        } catch (error) {
            console.error("Vehicle print failed:", error);
            alert(error?.message || "Unable to print vehicle details.");
        } finally {
            setPrinting(false);
        }
    };

    return (
        <div className="layout">
            <Sidebar />
            <div className="content">
                <Header />
                <div className="vehicles-page">

                    <div className="vehicles-stats-bar">
                        <div className="vehicles-stat-card">
                            <div className="vehicles-stat-text">
                                <span className="vehicles-stat-value">{filteredVehicles.length}</span>
                                <span className="vehicles-stat-label">Total Vehicles</span>
                            </div>
                        </div>
                        <div className="vehicles-stat-card vehicles-stat-card--ev">
                            <div className="vehicles-stat-text">
                                <span className="vehicles-stat-value">{evCount}</span>
                                <span className="vehicles-stat-label">EV Cabs</span>
                            </div>
                        </div>
                        <div className="vehicles-stat-card vehicles-stat-card--ice">
                            <div className="vehicles-stat-text">
                                <span className="vehicles-stat-value">{iceCount}</span>
                                <span className="vehicles-stat-label">ICE Cabs</span>
                            </div>
                        </div>
                        <div className="vehicles-stat-card vehicles-stat-card--active">
                            <div className="vehicles-stat-text">
                                <span className="vehicles-stat-value">{gpsCount}</span>
                                <span className="vehicles-stat-label">GPS Enabled</span>
                            </div>
                        </div>
                        <div className="vehicles-stat-card vehicles-stat-card--inactive">
                            <div className="vehicles-stat-text">
                                <span className="vehicles-stat-value">{filteredVehicles.length - gpsCount}</span>
                                <span className="vehicles-stat-label">No GPS</span>
                            </div>
                        </div>
                    </div>

                    {}
                    <div className="toolbar">
                        <div className="search-group">
                            <span className="search-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search vehicle number, display number, make, location, company…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className="clear-btn" onClick={() => setSearch("")}>✕</button>
                            )}
                        </div>

                        <div className="filter-dropdown-wrapper" ref={filterWrapperRef}>
                            <button
                                className={`filter-toggle-btn ${showFilters ? "filter-toggle-btn--active" : ""}`}
                                onClick={() => setShowFilters((s) => !s)}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 6h16M7 12h10M10 18h4" />
                                </svg>
                                Filters
                                {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
                            </button>

                            {showFilters && (
                                <div className="filter-panel">
                                    <div className="filter-field">
                                        <label>Service Location</label>
                                        <select
                                            value={filters.service_location_id}
                                            onChange={(e) => setFilter("service_location_id", e.target.value)}
                                        >
                                            <option value="">All Locations</option>
                                            {serviceLocations.map((x) => (
                                                <option key={x.id} value={String(x.id)}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Company</label>
                                        <select
                                            value={filters.company_id}
                                            onChange={(e) => setFilter("company_id", e.target.value)}
                                        >
                                            <option value="">All Companies</option>
                                            {companies.map((x) => (
                                                <option key={x.id} value={String(x.id)}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Make</label>
                                        <select
                                            value={filters.vehicle_make_id}
                                            onChange={(e) => {
                                                setFilter("vehicle_make_id", e.target.value);
                                                setFilter("vehicle_model_id", ""); 
                                            }}
                                        >
                                            <option value="">All Makes</option>
                                            {vehicleMakes.map((x) => (
                                                <option key={x.id} value={String(x.id)}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Model</label>
                                        <select
                                            value={filters.vehicle_model_id}
                                            onChange={(e) => setFilter("vehicle_model_id", e.target.value)}
                                            disabled={modelOptions.length === 0}
                                        >
                                            <option value="">All Models</option>
                                            {modelOptions.map((x) => (
                                                <option key={x.id} value={String(x.id)}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Fuel Type</label>
                                        <select
                                            value={filters.fuel_type_id}
                                            onChange={(e) => setFilter("fuel_type_id", e.target.value)}
                                        >
                                            <option value="">All Fuel Types</option>
                                            {fuelTypes.map((x) => (
                                                <option key={x.id} value={String(x.id)}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Active Status</label>
                                        <select
                                            value={filters.active_status}
                                            onChange={(e) => setFilter("active_status", e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>Year</label>
                                        <select
                                            value={filters.year_of_make}
                                            onChange={(e) => setFilter("year_of_make", e.target.value)}
                                        >
                                            <option value="">All Years</option>
                                            {yearOptions.map((y) => (
                                                <option key={y} value={String(y)}>{y}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-field">
                                        <label>GPS</label>
                                        <select
                                            value={filters.gps}
                                            onChange={(e) => setFilter("gps", e.target.value)}
                                        >
                                            <option value="">All Vehicles</option>
                                            <option value="yes">GPS Enabled</option>
                                            <option value="no">No GPS</option>
                                        </select>
                                    </div>

                                    <div className="filter-panel-actions">
                                        {activeFilterCount > 0 && (
                                            <button className="filter-clear-btn" onClick={clearFilters}>
                                                Clear all filters
                                            </button>
                                        )}
                                        <button className="filter-done-btn" onClick={() => setShowFilters(false)}>
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {}
                        <div className="fy-inline-group" ref={dateRangeWrapperRef}>
                            <label className="fy-inline-label">FY</label>
                            <select
                                className="fy-inline-select"
                                value={filters.financial_year_id}
                                onChange={(e) => {
                                    setFilter("financial_year_id", e.target.value);
                                    setDateFrom("");
                                    setDateTo("");
                                    setShowDateRange(false);
                                }}
                            >
                                <option value="">All Years</option>
                                {financialYears.map((fy) => (
                                    <option key={fy.id} value={String(fy.id)}>{fy.fy_label}</option>
                                ))}
                            </select>

                            {filters.financial_year_id && (
                                <button
                                    type="button"
                                    className={`fy-daterange-toggle ${showDateRange ? "fy-daterange-toggle--active" : ""} ${(dateFrom || dateTo) ? "fy-daterange-toggle--set" : ""}`}
                                    onClick={() => setShowDateRange((s) => !s)}
                                    title="Narrow by date"
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" />
                                        <path d="M16 2v4M8 2v4M3 10h18" />
                                    </svg>
                                    {dateFrom || dateTo ? (
                                        <span className="fy-daterange-toggle-label">
                                            {dateFrom ? formatDateOnly(dateFrom) : "…"} – {dateTo ? formatDateOnly(dateTo) : "…"}
                                        </span>
                                    ) : (
                                        <span className="fy-daterange-toggle-label">From / To</span>
                                    )}
                                    <svg width="10" height="10" viewBox="0 0 10 6" fill="none">
                                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            )}

                            {showDateRange && filters.financial_year_id && (
                                <div className="fy-daterange-popover">
                                    {fyActualRange ? (
                                        <>
                                            <div className="fy-daterange-field">
                                                <label>From</label>
                                                <input
                                                    type="date"
                                                    className="fy-daterange-input"
                                                    value={dateFrom}
                                                    min={formatISODate(fyActualRange.start)}
                                                    max={formatISODate(fyActualRange.end)}
                                                    onChange={(e) => setDateFrom(e.target.value)}
                                                />
                                            </div>
                                            <span className="fy-daterange-arrow">→</span>
                                            <div className="fy-daterange-field">
                                                <label>To</label>
                                                <input
                                                    type="date"
                                                    className="fy-daterange-input"
                                                    value={dateTo}
                                                    min={formatISODate(fyActualRange.start)}
                                                    max={formatISODate(fyActualRange.end)}
                                                    onChange={(e) => setDateTo(e.target.value)}
                                                />
                                            </div>
                                            {(dateFrom || dateTo) && (
                                                <button
                                                    type="button"
                                                    className="clear-btn"
                                                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                                                    title="Clear date range"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <span className="fy-inline-range fy-inline-range--muted">No vehicles yet</span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="toolbar-actions-right">
                            <button className="template-link" onClick={handleDownloadTemplate}>
                                Download
                            </button>

                            <label className={`upload-btn ${uploading ? "upload-btn--disabled" : ""}`}>
                                {uploading ? "Uploading…" : "Bulk Upload"}
                                <input type="file" accept=".xlsx,.xls,.csv"
                                    onChange={handleFileUpload} disabled={uploading || !canEditVehicles} hidden />
                            </label>

                            {canViewVehicles || canEditVehicles || canDeleteVehicles ? (
                                <button className="primary-btn" onClick={() => navigate("/add-vehicle")}>
                                    <span>+</span> Add Vehicle
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {}
                    {filterChips.length > 0 && (
                        <div className="filter-chips">
                            {filterChips.map((chip) => (
                                <span key={chip.key} className="filter-chip">
                                    {chip.label}
                                    <button onClick={() => clearOneFilter(chip.key)} aria-label={`Remove ${chip.label} filter`}>✕</button>
                                </span>
                            ))}
                            <button className="filter-chip filter-chip--clear" onClick={clearFilters}>
                                Clear all
                            </button>
                        </div>
                    )}

                    {}
                    {uploadResult && (
                        <div className="upload-summary">
                            <div className="upload-summary-header">
                                <p>
                                    ✅ <strong>{uploadResult.inserted}</strong> of{" "}
                                    <strong>{uploadResult.total_rows}</strong> vehicles imported.
                                    {uploadResult.failed > 0 && (
                                        <span className="upload-summary-failed"> ⚠️ {uploadResult.failed} failed.</span>
                                    )}
                                </p>
                                <button className="clear-btn" onClick={() => setUploadResult(null)}>✕</button>
                            </div>
                            {uploadResult.errors?.length > 0 && (
                                <ul className="upload-errors">
                                    {uploadResult.errors.map((e, i) => (
                                        <li key={i}><span className="error-row">Row {e.row}:</span> {e.reason}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <div className="table-card">
                        <div className="table-scroll">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Vehicle Code</th>
                                        <th>Photo</th>
                                        <th>Vehicle Number</th>
                                        <th>Display No</th>
                                        <th>Branch</th>
                                        <th>Company</th>
                                        <th>Model</th>
                                        <th>Year</th>
                                        <th>GPS</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="11" className="state-cell"><div className="spinner" /> Loading vehicles…</td></tr>
                                    ) : filteredVehicles.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="state-cell">
                                                <div className="empty-icon">🚌</div>
                                                No vehicles found
                                                {(search || activeFilterCount > 0) && (
                                                    <div className="state-cell-sub">Try adjusting your search or filters</div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedVehicles.map((v) => (
                                            <tr
                                                key={v.id}
                                                className={canViewVehicles ? "row-clickable" : ""}
                                                onClick={() => {
                                                    if (canViewVehicles) openViewModal(v);
                                                }}
                                                title={canViewVehicles ? "Click to view full details" : ""}
                                            >
                                                <td><span className="code-badge">{v.vehicle_code || "—"}</span></td>
                                                <td>
                                                    {v.vehicle_photo ? (
                                                        <img
                                                            src={photoUrl(v.vehicle_photo)}
                                                            alt={v.vehicle_registration_number}
                                                            className="vehicle-thumb"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setLightbox({
                                                                    url: photoUrl(v.vehicle_photo),
                                                                    vehicle: v,
                                                                });
                                                            }}
                                                            title="Click to view"
                                                        />
                                                    ) : (
                                                        <div className="vehicle-thumb vehicle-thumb--placeholder">🚌</div>
                                                    )}
                                                </td>
                                                <td><span className="code-badge">{v.vehicle_registration_number}</span></td>
                                                <td>{v.vehicle_display_number}</td>
                                                <td>{resolve(serviceLocations, v.service_location_id)}</td>
                                                <td>{resolve(companies, v.company_id)}</td>
                                                <td>{resolve(vehicleModels, v.vehicle_model_id)}</td>
                                                <td>{v.year_of_make}</td>
                                                <td>
                                                    <span className={`gps-pill ${v.gps_enabled ? "gps-pill--yes" : "gps-pill--no"}`}>
                                                        {v.gps_enabled ? "● GPS" : "○ No GPS"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-pill ${isVehicleActive(v) ? "status-pill--active" : "status-pill--inactive"}`}>
                                                        <span className="status-dot" />
                                                        {resolve(statuses, v.status_id)}
                                                    </span>
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="action-menu-wrapper">
                                                        <button
                                                            type="button"
                                                            ref={openActionMenu === v.id ? actionButtonRef : null}
                                                            className={`actions-btn ${openActionMenu === v.id ? "actions-btn--active" : ""}`}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                if (openActionMenu === v.id) {
                                                                    setOpenActionMenu(null);
                                                                    return;
                                                                }

                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                const menuWidth = 150;
                                                                const menuHeight = 130;
                                                                const gap = 6;

                                                                let left = rect.right - menuWidth;
                                                                let top = rect.bottom + gap;

                                                                if (left < 8) left = 8;
                                                                if (left + menuWidth > window.innerWidth - 8) {
                                                                    left = window.innerWidth - menuWidth - 8;
                                                                }

                                                                if (top + menuHeight > window.innerHeight - 8) {
                                                                    top = rect.top - menuHeight - gap;
                                                                }

                                                                if (top < 8) top = 8;

                                                                setActionMenuPos({
                                                                    top,
                                                                    left,
                                                                    width: Math.min(menuWidth, window.innerWidth - 16),
                                                                });
                                                                setOpenActionMenu(v.id);
                                                            }}
                                                            aria-label="Row actions"
                                                            aria-haspopup="true"
                                                            aria-expanded={openActionMenu === v.id}
                                                        >
                                                            Actions
                                                            <FiChevronDown size={14} />
                                                        </button>

                                                        {openActionMenu === v.id && createPortal(
                                                            <div
                                                                className="action-menu-dropdown action-menu-dropdown--portal"
                                                                ref={actionMenuRef}
                                                                style={{
                                                                    top: `${actionMenuPos.top}px`,
                                                                    left: `${actionMenuPos.left}px`,
                                                                    width: `${actionMenuPos.width}px`,
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {canViewVehicles && (

                                                                    <button

                                                                        type="button"

                                                                        className="action-menu-item"

                                                                        onClick={() => {

                                                                            setOpenActionMenu(null);

                                                                            navigate(`/add-vehicle/${v.id}?readOnly=true`);

                                                                        }}

                                                                    >

                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />

                                                                            <circle cx="12" cy="12" r="3" />

                                                                        </svg>

                                                                        View

                                                                    </button>

                                                                )}
                                                                {canEditVehicles && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="action-menu-item"
                                                                            onClick={() => {
                                                                                setOpenActionMenu(null);
                                                                                navigate(`/add-vehicle/${v.id}`);
                                                                            }}
                                                                        >
                                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <path d="M12 20h9" />
                                                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                                            </svg>
                                                                            Edit
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {canDeleteVehicles && (
                                                                    <button
                                                                        type="button"
                                                                        className="action-menu-item action-menu-item--delete"
                                                                        onClick={() => {
                                                                            setOpenActionMenu(null);
                                                                            handleDelete(v.id);
                                                                        }}
                                                                    >
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                            <path d="M3 6h18" />
                                                                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
                                                                        </svg>
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && filteredVehicles.length > 0 && (
                            <div className="table-footer">
                                <span className="pagination-summary">
                                    Showing <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–
                                    <strong>{Math.min(currentPage * PAGE_SIZE, filteredVehicles.length)}</strong> of{" "}
                                    <strong>{filteredVehicles.length}</strong>
                                    <span className="pagination-summary-suffix"> vehicles</span>
                                </span>

                                <div className="pagination-controls">
                                    <button
                                        type="button"
                                        className="pagination-arrow-btn"
                                        onClick={() => goToPage(currentPage - 10)}
                                        disabled={currentPage === 1}
                                        aria-label="Previous page"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>

                                    <span className="pagination-page-indicator">
                                        Page {currentPage} of {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        className="pagination-arrow-btn"
                                        onClick={() => goToPage(currentPage + 10)}
                                        disabled={currentPage === totalPages}
                                        aria-label="Next page"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {viewVehicle && (
                <>
                    <div className="vv2-overlay" onClick={closeViewModal}>
                        <div className="vv2-modal" onClick={(e) => e.stopPropagation()}>

                            <div className="vv2-header">
                                <div>
                                    <h3 className="vv2-header-title">
                                        {viewVehicle.vehicle_registration_number || viewVehicle.vehicle_code || "Vehicle"}
                                    </h3>
                                    <p className="vv2-header-sub">
                                        {viewVehicle.vehicle_code}
                                        {viewVehicle.vehicle_display_number && <>&nbsp;·&nbsp;{viewVehicle.vehicle_display_number}</>}
                                    </p>
                                </div>
                                <div className="vv2-header-actions">
                                    <button
                                        type="button"
                                        className="vv2-btn vv2-btn--light"
                                        onClick={handlePrintView}
                                        disabled={printing}
                                    >
                                        🖨 {printing ? "Preparing…" : "Print"}
                                    </button>
                                    <button
                                        type="button"
                                        className="vv2-btn vv2-btn--light"
                                        onClick={handleDownloadView}
                                        disabled={pdfGenerating}
                                    >
                                        ⬇ {pdfGenerating ? "Preparing…" : "Download"}
                                    </button>
                                    <button
                                        type="button"
                                        className="vv2-close-btn"
                                        onClick={closeViewModal}
                                        aria-label="Close"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>


                            <div className="vv2-tabs" role="tablist" aria-label="Vehicle details">
                                <button
                                    type="button"
                                    className={`vv2-tab ${viewVehicleTab === "information" ? "vv2-tab--active" : ""}`}
                                    onClick={() => setViewVehicleTab("information")}
                                    role="tab"
                                    aria-selected={viewVehicleTab === "information"}
                                >
                                    Vehicle Information
                                </button>
                                <button
                                    type="button"
                                    className={`vv2-tab ${viewVehicleTab === "documents" ? "vv2-tab--active" : ""}`}
                                    onClick={() => setViewVehicleTab("documents")}
                                    role="tab"
                                    aria-selected={viewVehicleTab === "documents"}
                                >
                                    Vehicle Documents
                                    {!viewVehicleDocsLoading && viewVehicleDocs && (
                                        <span className="vv2-tab-count">
                                            {VEHICLE_DOC_KEYS.filter((key) => viewVehicleDocs?.[`${key}_file_url`]).length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            <div className="vv2-tab-content">

                                {viewVehicleTab === "information" ? (
                            <div className="vv2-body">
                                <div>
                                    <div className="vv2-section">
                                        <h4 className="vv2-section-title">
                                            <span className="vv2-section-bar" />
                                            Vehicle Identity
                                        </h4>
                                        <div className="vv2-grid">
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Vehicle Code</span>
                                                <div className="vv2-field-value">{viewVehicle.vehicle_code || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Registration Number</span>
                                                <div className="vv2-field-value">{viewVehicle.vehicle_registration_number || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Display Number</span>
                                                <div className="vv2-field-value">{viewVehicle.vehicle_display_number || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Status</span>
                                                <div className="vv2-field-value">
                                                    <span className={`vv2-badge ${isVehicleActive(viewVehicle) ? "vv2-badge--active" : "vv2-badge--inactive"}`}>
                                                        <span className="dot" />
                                                        {resolve(statuses, viewVehicle.status_id)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="vv2-section">
                                        <h4 className="vv2-section-title">
                                            <span className="vv2-section-bar" />
                                            Company &amp; Location
                                        </h4>
                                        <div className="vv2-grid">
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Company</span>
                                                <div className="vv2-field-value">{resolve(companies, viewVehicle.company_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Service Location</span>
                                                <div className="vv2-field-value">{resolve(serviceLocations, viewVehicle.service_location_id)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="vv2-section">
                                        <h4 className="vv2-section-title">
                                            <span className="vv2-section-bar" />
                                            Make &amp; Specifications
                                        </h4>
                                        <div className="vv2-grid">
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Make</span>
                                                <div className="vv2-field-value">{resolve(vehicleMakes, viewVehicle.vehicle_make_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Model</span>
                                                <div className="vv2-field-value">{resolve(vehicleModels, viewVehicle.vehicle_model_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Fuel Type</span>
                                                <div className="vv2-field-value">{resolve(fuelTypes, viewVehicle.fuel_type_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Transmission</span>
                                                <div className="vv2-field-value">{resolve(transmissionTypes, viewVehicle.transmission_type_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Year of Make</span>
                                                <div className="vv2-field-value">{viewVehicle.year_of_make || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Seating Capacity</span>
                                                <div className="vv2-field-value">{viewVehicle.seating_capacity || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Average Mileage</span>
                                                <div className="vv2-field-value">{viewVehicle.average_mileage || "-"}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="vv2-section">
                                        <h4 className="vv2-section-title">
                                            <span className="vv2-section-bar" />
                                            Compliance &amp; Identifiers
                                        </h4>
                                        <div className="vv2-grid">
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Tax Status</span>
                                                <div className="vv2-field-value">{resolve(taxStatuses, viewVehicle.tax_status_id)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Engine Number</span>
                                                <div className="vv2-field-value">{viewVehicle.engine_number || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Chassis Number</span>
                                                <div className="vv2-field-value">{viewVehicle.chassis_number || "-"}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">GPS</span>
                                                <div className="vv2-field-value">
                                                    <span className={`vv2-badge ${viewVehicle.gps_enabled ? "vv2-badge--gps-yes" : "vv2-badge--gps-no"}`}>
                                                        {viewVehicle.gps_enabled ? "● GPS Enabled" : "○ No GPS"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="vv2-section">
                                        <h4 className="vv2-section-title">
                                            <span className="vv2-section-bar" />
                                            Record Info
                                        </h4>
                                        <div className="vv2-grid">
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Created On</span>
                                                <div className="vv2-field-value">{formatDateTime(viewVehicle.created_at)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Updated On</span>
                                                <div className="vv2-field-value">{formatDateTime(viewVehicle.updated_at)}</div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Created By</span>
                                                <div className="vv2-field-value">
                                                    {viewVehicle.created_by_name || (viewVehicle.created_by ? `User #${viewVehicle.created_by}` : "-")}
                                                </div>
                                            </div>
                                            <div className="vv2-field">
                                                <span className="vv2-field-label">Updated By</span>
                                                <div className="vv2-field-value">
                                                    {viewVehicle.updated_by_name || (viewVehicle.updated_by ? `User #${viewVehicle.updated_by}` : "-")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="vv2-photo-col">
                                    {viewVehicle.vehicle_photo ? (
                                        <button
                                            type="button"
                                            className="vv2-photo-card"
                                            onClick={() => setLightbox({
                                                url: photoUrl(viewVehicle.vehicle_photo),
                                                vehicle: viewVehicle,
                                            })}
                                            title="Click to enlarge"
                                        >
                                            <img
                                                src={photoUrl(viewVehicle.vehicle_photo)}
                                                alt={viewVehicle.vehicle_registration_number}
                                            />
                                        </button>
                                    ) : (
                                        <div className="vv2-photo-card">
                                            <div className="vv2-photo-placeholder">
                                                <span className="icon">🚌</span>
                                                <span>No photo uploaded</span>
                                            </div>
                                        </div>
                                    )}
                                    <span className="vv2-photo-caption">Vehicle Photo</span>
                                </div>
                            </div>
                        ) : (

                                <div className="vv2-documents-panel">
                                    <div className="vv2-documents-intro">
                                        <div>
                                            <h4 className="vv2-documents-title">Vehicle Documents</h4>
                                            <p className="vv2-documents-subtitle">
                                                Documents uploaded for {viewVehicle.vehicle_registration_number || viewVehicle.vehicle_code || "this vehicle"}.
                                            </p>
                                        </div>
                                        <span className="vv2-documents-count">
                                            {viewVehicleDocsLoading
                                                ? "Loading…"
                                                : `${VEHICLE_DOC_KEYS.filter((key) => viewVehicleDocs?.[`${key}_file_url`]).length}/${VEHICLE_DOC_KEYS.length} uploaded`}
                                        </span>
                                    </div>

                                    {viewVehicleDocsLoading ? (
                                        <div className="vv2-documents-loading">
                                            <span className="vv2-doc-spinner" />
                                            <span>Loading vehicle documents…</span>
                                        </div>
                                    ) : (
                                        <div className="vv2-documents-list">
                                            {VEHICLE_DOC_KEYS.map((key) => {
                                                const url = viewVehicleDocs?.[`${key}_file_url`] || "";
                                                const expiry = viewVehicleDocs?.[`${key}_expiry_date`] || "";
                                                const resolvedUrl = url ? photoUrl(url) : "";
                                                const isImage = isImageDocUrl(resolvedUrl);
                                                const isPdf = isPdfDocUrl(resolvedUrl);

                                                const document = {
                                                    key,
                                                    label: VEHICLE_DOC_LABELS[key],
                                                    url: resolvedUrl,
                                                    expiry,
                                                    isImage,
                                                    isPdf,
                                                    expiryClass: getDocumentExpiryClass(expiry),
                                                };

                                                return (
                                                    <div className={`vv2-document-row ${url ? "vv2-document-row--uploaded" : "vv2-document-row--missing"}`} key={key}>
                                                        <div className="vv2-document-type-icon">
                                                            {isImage ? "IMG" : isPdf ? "PDF" : url ? "FILE" : "—"}
                                                        </div>

                                                        <div className="vv2-document-main">
                                                            <div className="vv2-document-name">{document.label}</div>
                                                            <div className="vv2-document-meta">
                                                                <span className={`vv2-doc-status ${url ? "uploaded" : "not-uploaded"}`}>
                                                                    {url ? "Uploaded" : "Not uploaded"}
                                                                </span>
                                                                {expiry && (
                                                                    <span className={`vv2-doc-expiry ${document.expiryClass}`}>
                                                                        Expiry: {formatDateOnly(expiry)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="vv2-document-actions">
                                                            {url ? (
                                                                <button
                                                                    type="button"
                                                                    className="vv2-document-view-btn"
                                                                    onClick={() => openDocumentViewer(document)}
                                                                >
                                                                    View Document
                                                                </button>
                                                            ) : (
                                                                <span className="vv2-document-unavailable">No file</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                        )}
                            </div>

                        </div>
                    </div>
                </>
            )}

            {docViewer && (
                <div
                    className="vv2-document-viewer-overlay"
                    onClick={closeDocumentViewer}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${docViewer.label} preview`}
                >
                    <div
                        className="vv2-document-viewer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vv2-document-viewer-header">
                            <div>
                                <h3>{docViewer.label}</h3>
                                <span>
                                    {viewVehicle?.vehicle_registration_number || viewVehicle?.vehicle_code || "Vehicle"}
                                </span>
                            </div>

                            <div className="vv2-document-viewer-header-actions">
                                <button
                                    type="button"
                                    className="vv2-document-viewer-close"
                                    onClick={closeDocumentViewer}
                                    aria-label="Close document preview"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="vv2-document-viewer-body">
                            {docViewer.isImage ? (
                                <img
                                    src={docViewer.url}
                                    alt={docViewer.label}
                                    className="vv2-document-preview-image"
                                />
                            ) : (
                                <iframe
                                    src={docViewer.url}
                                    title={docViewer.label}
                                    className="vv2-document-preview-frame"
                                />
                            )}
                        </div>

                        <div className="vv2-document-viewer-footer">
                            <span>
                                {docViewer.isPdf
                                    ? "PDF document"
                                    : docViewer.isImage
                                        ? "Image document"
                                        : "Document preview"}
                            </span>

                            {docViewer.url && (
                                <div className="vv2-document-viewer-footer-actions">
                                    <button
                                        type="button"
                                        className="vv2-document-footer-btn vv2-document-footer-btn--print"
                                        onClick={handleDocumentPrint}
                                    >
                                        🖨 Print
                                    </button>
                                    <button
                                        type="button"
                                        className="vv2-document-footer-btn vv2-document-footer-btn--download"
                                        onClick={handleDocumentDownload}
                                    >
                                        ⬇ Download
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {lightbox && createPortal(
                <div
                    className="vehicle-photo-modal"
                    onClick={() => setLightbox(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Vehicle photo preview"
                >
                    <div
                        className="vehicle-photo-modal__content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="vehicle-photo-modal__close"
                            onClick={() => setLightbox(null)}
                            aria-label="Close vehicle photo"
                        >
                            ✕
                        </button>
                        <img
                            src={lightbox.url}
                            alt="Vehicle"
                            className="vehicle-photo-modal__image"
                        />
                    </div>
                </div>,
                document.body
            )}


            {toast && (
                <div
                    className="vehicles-toast-overlay"
                    style={{
                        position: "fixed",
                        bottom: "28px",
                        right: "28px",
                        zIndex: 3000,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        minWidth: "260px",
                        maxWidth: "380px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: 500,
                        background: toast.type === "success" ? "#16a34a" : "#dc2626",
                        animation: "vehiclesToastSlideIn 0.25s ease-out",
                    }}
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>
                        {toast.type === "success" ? "✓" : "⚠"}
                    </span>
                    <span style={{ flex: 1 }}>{toast.text}</span>
                    <button
                        type="button"
                        onClick={dismissToast}
                        aria-label="Dismiss"
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#fff",
                            opacity: 0.85,
                            cursor: "pointer",
                            fontSize: "15px",
                            lineHeight: 1,
                            padding: "2px",
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}
            <style>{`
                @keyframes vehiclesToastSlideIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}


